import { useState, ChangeEvent, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2, CheckCircle2, AlertCircle } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

const SAMPLE_JSON = `{
  "siteName": "247 DevTools Hub",
  "seoOptimized": true,
  "developerProductivity": {
    "category": "Offline Utility Suite",
    "privacyGuaranteed": true,
    "activeUsers": 12500,
    "uptime": "99.99%",
    "supportedLanguages": [
      "JSON",
      "XML",
      "YAML",
      "CSV",
      "SQL",
      "HTML",
      "CSS",
      "JS"
    ]
  },
  "localRAMStorage": {
    "engine": "In-Browser Client-Side Node",
    "cookiesEnabled": false,
    "encryptionLevel": "AES-256-Local"
  },
  "recentFeatures": [
    {
      "id": "excel-grid-01",
      "name": "Advanced Formula Editor",
      "formulas": ["SUM", "AVERAGE", "IF", "UPPER"]
    },
    {
      "id": "bcrypt-02",
      "name": "Secure Password Hashing",
      "rounds": 10
    }
  ]
}`;

export function JsonFormatter() {
  const [input, setInput] = useState(SAMPLE_JSON);
  const [output, setOutput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [indent, setIndent] = useState(2);
  const [copied, setCopied] = useState(false);

  const formatJson = (val: string, spaces: number) => {
    if (!val.trim()) {
      setOutput("");
      setError(null);
      return;
    }
    try {
      const parsed = JSON.parse(val);
      setOutput(JSON.stringify(parsed, null, spaces));
      setError(null);
    } catch (e: any) {
      setError(e.message || "Invalid JSON");
    }
  };

  useEffect(() => {
    formatJson(SAMPLE_JSON, 2);
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    formatJson(val, indent);
  };

  const handleIndentChange = (spaces: number) => {
    setIndent(spaces);
    formatJson(input, spaces);
  };

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="JSON Formatter & Validator Online"
      description="Format, validate, and beautify your JSON code instantly. Create readable JSON with our free online tool."
      h1="JSON Formatter"
      category="JSON Tools"
      categoryPath="/json-tools"
      howToUse={[
        "Paste your raw JSON into the input area.",
        "Select your preferred indentation (2 or 4 spaces) or minify the JSON.",
        "View the validated and formatted JSON in the output area."
      ]}
      faq={[
        { question: "How to format JSON?", answer: "Paste your code into our JSON formatter tool to automatically beautify and validate it." },
        { question: "Is my JSON valid?", answer: "Our tool validates your JSON in real-time and alerts you of any syntax errors." }
      ]}
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-slate-700">Indent:</span>
          <div className="flex gap-2">
            {[2, 4].map((spaces) => (
              <Button
                key={spaces}
                onClick={() => handleIndentChange(spaces)}
                variant={indent === spaces ? "default" : "outline"}
                size="sm"
              >
                {spaces} Spaces
              </Button>
            ))}
            <Button
              onClick={() => handleIndentChange(0)}
              variant={indent === 0 ? "default" : "outline"}
              size="sm"
            >
              Compact (Minify)
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Input JSON</label>
            <Textarea
              placeholder='{"hello": "world"}'
              className="min-h-[400px]"
              value={input}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">Output JSON</label>
              {error ? (
                <span className="text-sm text-red-600 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Invalid JSON</span>
              ) : output ? (
                <span className="text-sm text-green-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Valid JSON</span>
              ) : null}
            </div>
            <Textarea
              className="min-h-[400px] bg-slate-50"
              value={error ? error : output}
              readOnly
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleCopy} disabled={!!error || !output} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Output"}
          </Button>
          <Button onClick={() => { setInput(""); setOutput(""); setError(null); }} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
